/**
 * Kusto / Azure Data Explorer dashboard templates (S7, remediated S7-R). No
 * pre-existing SQL catalog to source from (G8: Kusto has no Session Browser
 * / Performance Dashboard provider) — authored from scratch around the
 * `.show`-only management-command policy (Docs/DbEngineKusto.md §3/§4.1).
 *
 * BARE `.show` templates (dot-prefixed, pipe-free) omit the
 * `{{dashboard_filters}}` slot entirely (a control command with no query
 * stage has no WHERE for S11 to compose into); the catalog integrity test
 * enforces exactly that boundary. Piping after `.show` IS legal — the pipe
 * tail is ordinary query grammar, so every PIPED `.show` template below
 * carries the `{{dashboard_filters}}` slot in its tail like any query
 * (Codex review 2026-07-17: the earlier any-leading-dot slot exemption
 * over-exempted the piped ones) — but pipes work only on a real cluster:
 * the Azure Monitor proxy's narrower management grammar 400s any pipe after
 * a control command (Docs/DbEngineKusto.md §4.1; our own adapter's
 * `classifyKustoStatement` only checks the `.show` prefix and never blocks
 * the pipe — `electron/query-engine-adapters/kusto/kustoStatements.ts`).
 * Every piped `.show` template (ingestion failures — M22; cluster
 * diagnostics + tables by extent count — the 2026-07-17 Health additions)
 * is therefore gated `requiredCapabilities: ['kusto-cluster']`.
 *
 * `kusto.jobs.ingestion_by_table` is the catalog's one kusto *query*
 * template (no leading dot): KQL query grammar allows pipes on BOTH endpoint
 * kinds, so it is proxy-safe AND carries the `{{dashboard_filters}}` slot —
 * `parameterBinding.ts` composes the kusto dialect for it (`true` tautology,
 * `==` predicates, `['col']` quoting). Being schema-agnostic forces
 * `union withsource … *`; see the honesty caveats on the template itself
 * (ingestion_time() is approximate metadata, and the union fan-out has a
 * real cost on databases with very many tables).
 *
 * All 2026-07-17 changes were live-verified against the real e2e cluster
 * AND a real App Insights proxy (service-principal / az-cli probes): the
 * piped `.show` health templates pass on the cluster (verbatim-from-file,
 * `{{dashboard_filters}}` → `true` exactly as the binding substitutes) and
 * 400 on the proxy; the union query passes on both (real predicate +
 * tautology forms). The same probe showed `.show cluster` — although it IS
 * in the proxy's documented verb list (§4.1) — 400s on a real proxy
 * (grammar-listed does not mean implemented), so `cluster_nodes` is now
 * cluster-gated too instead of hard-failing loudly on a proxy (same M21
 * quality bar). The Codex-review pass replaced the `.show extents`
 * fragmentation template with a `.show tables details` one — Microsoft
 * documents database-wide `.show extents` as unsuitable for periodic
 * monitoring (it can exceed internal limits once a database holds millions
 * of extents) and recommends `.show tables details` for extent-count
 * monitoring; column names were live-verified, not taken from docs.
 *
 * No kusto template uses a `stat` tile: `statValueColumn` must appear as an
 * `AS <alias>` in the SQL (catalog integrity test) and KQL projection has no
 * `AS` column alias (`project X = expr`), so single-row snapshots ship as
 * one-row tables instead.
 *
 * `materialized-views` and `external tables` are ALSO cluster-only (missing
 * from the proxy's narrower verb list) and are now genuinely HIDDEN on a
 * proxy connection via the same capability (S7-R M21) — the S7 catalog
 * shipped them ungated, which meant they hard-failed loudly on a proxy
 * instead of being hidden per the plan's quality bar; that gap is now
 * closed, not a carry item anymore.
 *
 * `kusto.performance.build_version` was CUT (S7-R M20): its description
 * claimed per-node rolling-upgrade confirmation, but `.show version`
 * returns exactly ONE service row — that claim was factually wrong.
 *
 * No `indexes`/`backup` categories: Kusto is append-only analytics with no
 * index or backup-service surface — absence is correct here, not a gap.
 */
import type { DashboardTemplate } from '../templateTypes';

export const kustoTemplates: DashboardTemplate[] = [
  {
    id: 'kusto.storage.capacity_by_resource',
    engine: 'kusto',
    title: 'Cluster capacity by resource',
    description: 'Ingestion, extent and data capacity consumed vs. total, per resource — a resource near its ceiling throttles queries and ingestion.',
    category: 'storage',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No capacity data returned for this cluster.',
    sql: `.show capacity`,
  },
  {
    id: 'kusto.performance.cluster_nodes',
    engine: 'kusto',
    title: 'Cluster node health',
    description: 'Cluster nodes and their roles — spot a node that dropped out or is unhealthy before it affects query latency. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'performance',
    fits: ['table'],
    defaultTileType: 'table',
    // `.show cluster` is in the proxy's documented verb list yet a real App
    // Insights proxy answers HTTP 400 (live probe 2026-07-17) — gated so the
    // template is hidden on a proxy instead of hard-failing.
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'rows',
    emptyStateCopy: 'No nodes reported for this cluster.',
    sql: `.show cluster`,
  },
  {
    id: 'kusto.performance.cluster_diagnostics',
    engine: 'kusto',
    title: 'Cluster diagnostics snapshot',
    description: 'One-row cluster health check — overall health, offline machines, ingestion/merge success rates and failed operations; anything below healthy/100% wants attention. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'performance',
    fits: ['table'],
    defaultTileType: 'table',
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'scalar',
    emptyStateCopy: 'No diagnostics row returned for this cluster.',
    // The pipe tail is query grammar, so the filter slot lives after the
    // projection (`| where true` there is valid KQL — live-verified).
    sql: `.show diagnostics
| project IsHealthy, NotHealthyReason, IsAttentionRequired, AttentionRequiredReason, MachinesTotal, MachinesOffline, IngestionsSuccessRate, MergesSuccessRate, FailedIngestOperations, FailedMergeOperations
| where {{dashboard_filters}}`,
  },
  {
    id: 'kusto.performance.tables_by_extent_count',
    engine: 'kusto',
    title: 'Tables by extent count',
    description: 'Extent count, rows and size per table (top 15 by extent count) — a table accumulating many small extents signals merge or batching lag; extent size includes index size, original size is the pre-compression data. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'performance',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'rows',
    emptyStateCopy: 'No tables exist in this database yet.',
    // Codex review 2026-07-17 (HIGH): database-wide `.show extents` is
    // documented as unsuitable for periodic monitoring (can exceed internal
    // limits on millions of extents) — `.show tables details` is Microsoft's
    // recommended surface for extent-count monitoring. TotalExtentSize
    // includes index size (hence the honest ExtentSizeMB alias next to
    // OriginalSizeMB). Column names live-verified against the real cluster.
    sql: `.show tables details
| project TableName, TotalExtents, TotalRowCount, ExtentSizeMB = round(TotalExtentSize / 1048576.0, 1), OriginalSizeMB = round(TotalOriginalSize / 1048576.0, 1)
| where {{dashboard_filters}}
| top 15 by TotalExtents`,
  },
  {
    id: 'kusto.storage.database_inventory',
    engine: 'kusto',
    title: 'Database inventory',
    description: 'Every database visible on this connection with its access mode — catch an unexpected new or read-only database.',
    category: 'storage',
    fits: ['table'],
    defaultTileType: 'table',
    expectedShape: 'rows',
    emptyStateCopy: 'No databases visible on this connection.',
    sql: `.show databases`,
  },
  {
    id: 'kusto.storage.materialized_views',
    engine: 'kusto',
    title: 'Materialized views',
    description: 'Materialized views and their backfill/last-run state — a stalled materialization means downstream queries serve stale aggregates. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'storage',
    fits: ['table'],
    defaultTileType: 'table',
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'rows',
    emptyStateCopy: 'No materialized views defined on this database.',
    sql: `.show materialized-views`,
  },
  {
    id: 'kusto.storage.external_tables',
    engine: 'kusto',
    title: 'External tables',
    description: 'External tables (ADLS/Blob-backed) and their schema/format/partitioning properties — a misconfigured external table fails every query that references it. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'storage',
    fits: ['table'],
    defaultTileType: 'table',
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'rows',
    emptyStateCopy: 'No external tables defined on this database.',
    sql: `.show external tables`,
  },
  {
    id: 'kusto.sessions.running_queries',
    engine: 'kusto',
    title: 'Running queries',
    description: 'Queries currently executing on the cluster — the closest thing ADX has to a sessions view; spot a runaway query before it starves everything else. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'sessions',
    fits: ['table'],
    defaultTileType: 'table',
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'rows',
    emptyStateCopy: 'No queries are currently running on this cluster.',
    sql: `.show queries`,
  },
  {
    id: 'kusto.jobs.ingestion_failures',
    engine: 'kusto',
    title: 'Ingestion failures (last 24h)',
    description: 'Ingestion attempts that failed in the last day — a broken ingestion pipeline means downstream tables silently stop receiving data. Cluster connections only — not available on an App Insights / Log Analytics proxy.',
    category: 'jobs',
    fits: ['table'],
    defaultTileType: 'table',
    requiredCapabilities: ['kusto-cluster'],
    expectedShape: 'rows',
    emptyStateCopy: 'No ingestion failures in the last 24 hours.',
    sql: `.show ingestion failures | where FailedOn > ago(1d) | where {{dashboard_filters}}`,
  },
  {
    id: 'kusto.jobs.ingestion_by_table',
    engine: 'kusto',
    title: 'Recent ingestion by table (approx., last hour)',
    description: 'Approximate rows landed per table in the last hour, from ingestion_time() metadata — records ingested while the IngestionTime policy was off carry no ingestion time and are invisible here, a table with no qualifying rows is absent entirely (absence is not proof it went quiet), and the union across every table can be costly on databases with very many tables (prefer a longer refresh interval); works on clusters and App Insights / Log Analytics connections alike.',
    category: 'jobs',
    fits: ['chart', 'table'],
    defaultTileType: 'chart',
    expectedShape: 'rows',
    emptyStateCopy: 'No rows with ingestion-time metadata landed in the last hour — data ingested while the IngestionTime policy was off has none, so this can be empty even when ingestion happened.',
    // A KQL *query* (not a control command): schema-agnostic via `union *`.
    // The ingestion_time() bound narrows the scan to recent data in the
    // common case, but the union fan-out still touches every table, and rows
    // without ingestion-time metadata never qualify — this is an
    // approximation, not an ingestion ledger (Codex review 2026-07-17).
    // Carries a live {{dashboard_filters}} slot (kusto-dialect composition —
    // parameterBinding.ts).
    sql: `union withsource = TableName *
| where ingestion_time() > ago(1h)
| where {{dashboard_filters}}
| summarize RowsIngested = count() by TableName
| order by RowsIngested desc`,
  },
];
